import { Component, OnInit, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { PricesService } from '../../../../core/services/prices.service';
import { TimezonesService } from '../../../../core/services/timezones.service';
import { CommercialZonesService } from '../../../../core/services/commercial-zones.service';
import { CommercialConditionsService } from '../../../../core/services/commercial-conditions.service';
import { LoadingService } from '../../../../core/services/loading.service';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { FloatLabelModule } from 'primeng/floatlabel';
import { SelectModule } from 'primeng/select';
import { InputNumberModule } from 'primeng/inputnumber';
import { DatePickerModule } from 'primeng/datepicker';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { SkeletonModule } from 'primeng/skeleton';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { CheckboxModule } from 'primeng/checkbox';
import {
  UiDayKey, ApiDayKey, UI_TO_API_DAY, API_TO_UI_DAY,
  PriceFormData, PriceScheduleFormItem, PriceDetail, PriceScheduleDetail
} from '../../../../core/models/price.model';

interface PriceMatrixRow {
  id: string;
  startTime: string;
  endTime: string;
  prices: { [day in UiDayKey]: number };
}

/**
 * Formulario de configuración de precios (página completa, rutas create/edit).
 *
 * Una configuración se identifica por zona horaria + zona comercial + condición comercial y
 * se captura en tres pasos:
 * 1. Identificadores (nombre y las tres llaves anteriores).
 * 2. Matriz de precios: franjas horarias × días de la semana. Las franjas deben cubrir
 *    las 24 h, iniciar a las 00:00 y no sobreponerse (`validateTimeSlots`).
 * 3. Vigencia (fecha de inicio y fin opcional), IVA y estado activo.
 *
 * Con `?duplicateFrom={id}` carga una configuración existente como base de una nueva.
 * Si el API responde 409 `PRICE_CONFIG_VALIDITY_GAPS_DETECTED` (la vigencia deja huecos
 * respecto a otras tarifas), se pide confirmación y se reenvía con `confirmGaps = true`.
 */
@Component({
  selector: 'app-price-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ToastModule,
    ButtonModule,
    InputTextModule,
    FloatLabelModule,
    SelectModule,
    InputNumberModule,
    DatePickerModule,
    ToggleSwitchModule,
    TooltipModule,
    SkeletonModule,
    ConfirmDialogModule,
    CheckboxModule
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './price-form.component.html',
  styleUrl: './price-form.component.scss'
})
export class PriceFormComponent implements OnInit {

  priceId: number | null = null;
  isEditMode = signal<boolean>(false);
  loadingData = signal<boolean>(false);

  // Paso 1: Identificadores
  configName = signal<string>('');
  selectedTimezone = signal<number | null>(null);
  selectedCommercialZone = signal<number | null>(null);
  selectedCommercialCondition = signal<number | null>(null);

  // Computed: verificar si identificadores están completos
  identifiersComplete = computed(() => {
    return !!(
      this.configName() &&
      this.selectedTimezone() &&
      this.selectedCommercialZone() &&
      this.selectedCommercialCondition()
    );
  });

  // Paso 2: Matriz de Precios
  daysOfWeek: UiDayKey[] = ['lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado', 'domingo'];
  daysLabels = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
  priceMatrix = signal<PriceMatrixRow[]>([
    {
      id: '1',
      startTime: '00:00',
      endTime: '06:00',
      prices: { lunes: 0, martes: 0, miercoles: 0, jueves: 0, viernes: 0, sabado: 0, domingo: 0 }
    },
    {
      id: '2',
      startTime: '06:00',
      endTime: '12:00',
      prices: { lunes: 0, martes: 0, miercoles: 0, jueves: 0, viernes: 0, sabado: 0, domingo: 0 }
    },
    {
      id: '3',
      startTime: '12:00',
      endTime: '18:00',
      prices: { lunes: 0, martes: 0, miercoles: 0, jueves: 0, viernes: 0, sabado: 0, domingo: 0 }
    },
    {
      id: '4',
      startTime: '18:00',
      endTime: '00:00',
      prices: { lunes: 0, martes: 0, miercoles: 0, jueves: 0, viernes: 0, sabado: 0, domingo: 0 }
    }
  ]);

  // Paso 3: Vigencia y Activación
  validityStartDate = signal<Date>(new Date());
  validityEndDate = signal<Date | null>(null);
  hasEndDate = signal<boolean>(false);
  taxPercentage = signal<number>(16);
  isActive = signal<boolean>(false);

  // Opciones de selectores
  timezoneOptions = signal<{ label: string; value: number }[]>([]);
  commercialZoneOptions = signal<{ label: string; value: number }[]>([]);
  commercialConditionOptions = signal<{ label: string; value: number }[]>([]);

  touchedFields = signal<Set<string>>(new Set());

  constructor(
    private pricesService: PricesService,
    private timezonesService: TimezonesService,
    private commercialZonesService: CommercialZonesService,
    private commercialConditionsService: CommercialConditionsService,
    private loadingService: LoadingService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.loadCatalogs();

    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam) {
      this.priceId = Number(idParam);
      this.isEditMode.set(true);
      this.loadConfiguration(this.priceId);
      return;
    }

    const duplicateFrom = this.route.snapshot.queryParamMap.get('duplicateFrom');
    if (duplicateFrom) {
      this.loadConfiguration(Number(duplicateFrom), true); // carga datos pero priceId/isEditMode quedan en null/false → se crea como nuevo
    }
  }

  private loadConfiguration(id: number, isDuplicate = false): void {
    this.loadingData.set(true);
    this.loadingService.show();

    this.pricesService.getById(id).subscribe({
      next: (detail: PriceDetail) => {
        this.configName.set(isDuplicate ? `${detail.name} - Duplicado` : detail.name);
        this.selectedTimezone.set(detail.timezone_id);
        this.selectedCommercialZone.set(detail.commercial_zone_id);
        this.selectedCommercialCondition.set(detail.commercial_condition_id);

        this.validityStartDate.set(this.parseDate(detail.validity_start_date) || new Date());

        if (detail.validity_end_date) {
          this.validityEndDate.set(this.parseDate(detail.validity_end_date));
          this.hasEndDate.set(true);
        } else {
          this.validityEndDate.set(null);
          this.hasEndDate.set(false);
        }

        this.taxPercentage.set(detail.tax_percentage);
        this.isActive.set(detail.is_active);
        this.priceMatrix.set(this.buildMatrixFromSchedule(detail.schedule));

        this.loadingData.set(false);
        this.loadingService.hide();
      },
      error: (error) => {
        console.error('Error loading configuration:', error);
        this.loadingData.set(false);
        this.loadingService.hide();
        this.router.navigate(['/price-configuration']);
      }
    });
  }

  /**
   * Construye la matriz del formulario desde el schedule del API (GetById).
   * El API devuelve start_time/end_time como objetos { hour, minute, second, nano }
   * y precios con claves en inglés (monday, tuesday…).
   */
  private buildMatrixFromSchedule(schedule: PriceScheduleDetail[]): PriceMatrixRow[] {
    return schedule.map((item, index) => {
      const startTime = this.apiTimeToFormTime(item.start_time, false);
      const endTime   = this.apiTimeToFormTime(item.end_time, true);

      const prices = { lunes: 0, martes: 0, miercoles: 0, jueves: 0, viernes: 0, sabado: 0, domingo: 0 };
      (Object.keys(API_TO_UI_DAY) as ApiDayKey[]).forEach(apiDay => {
        const uiDay = API_TO_UI_DAY[apiDay];
        prices[uiDay] = item.prices[apiDay] ?? 0;
      });

      return { id: (index + 1).toString(), startTime, endTime, prices };
    });
  }

  /**
   * Convierte un string de tiempo del API "HH:MM:SS" a "HH:MM" para el formulario.
   * Para end_time se añade 1 segundo (el API usa tiempos inclusivos, p.ej. "05:59:59")
   * y se convierte al inicio del siguiente intervalo: "05:59:59" → "06:00".
   */
  private apiTimeToFormTime(timeStr: string, isEndTime: boolean): string {
    const [h, m, s] = timeStr.split(':').map(Number);
    let totalSeconds = h * 3600 + m * 60 + s;
    if (isEndTime) {
      totalSeconds += 1; // "05:59:59" + 1s → "06:00:00"
    }
    if (totalSeconds >= 86400) return '00:00'; // media noche (fin del día)
    const newH = Math.floor(totalSeconds / 3600).toString().padStart(2, '0');
    const newM = Math.floor((totalSeconds % 3600) / 60).toString().padStart(2, '0');
    return `${newH}:${newM}`;
  }

  /**
   * Convierte el tiempo del formulario "HH:MM" al formato de cadena del API "HH:MM:SS".
   * Para end_time se resta 1 segundo (el API usa tiempos inclusivos).
   *   "12:00" (end) → "11:59:59"
   *   "00:00" (end) → "23:59:59"
   */
  private formTimeToApiString(formTime: string, isEndTime: boolean): string {
    if (!isEndTime) return `${formTime}:00`;
    if (formTime === '00:00') return '23:59:59';
    const [h, m] = formTime.split(':').map(Number);
    const totalSeconds = h * 3600 + m * 60 - 1;
    const newH = Math.floor(totalSeconds / 3600).toString().padStart(2, '0');
    const newM = Math.floor((totalSeconds % 3600) / 60).toString().padStart(2, '0');
    const newS = (totalSeconds % 60).toString().padStart(2, '0');
    return `${newH}:${newM}:${newS}`;
  }

  private parseDate(dateStr: string): Date | null {
    // Soporta tanto 'YYYY-MM-DD' como 'YYYY-MM-DDTHH:mm:ssZ'
    const datePart = dateStr.split('T')[0];
    const parts = datePart.split('-').map(Number);
    if (parts.length !== 3) return null;
    const [year, month, day] = parts;
    return new Date(year, month - 1, day);
  }

  minEndDate = computed(() => {
    const base = this.validityStartDate();
    if (!base) return new Date();
    const min = new Date(base);
    min.setDate(min.getDate() + 1);
    min.setHours(0, 0, 0, 0);
    return min;
  });

  private isEndDateValid(): boolean {
    if (!this.hasEndDate() || !this.validityEndDate()) return true;
    const start = new Date(this.validityStartDate());
    const end = new Date(this.validityEndDate()!);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    return end.getTime() > start.getTime();
  }

  private parseTimeToMinutes(time: string): number | null {
    const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(time);
    if (!match) return null;
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    return hours * 60 + minutes;
  }

  private formatTime12(time: string): string {
    const parts = time.split(':').map(Number);
    if (parts.length < 2 || Number.isNaN(parts[0]) || Number.isNaN(parts[1])) return time;
    const [hour24, minute] = parts;
    const period = hour24 >= 12 ? 'p.m.' : 'a.m.';
    const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
    const minuteStr = minute.toString().padStart(2, '0');
    return `${hour12}:${minuteStr} ${period}`;
  }

  /** Valida que las franjas cubran 00:00–24:00 sin huecos ni traslapes (una hora fin 00:00 equivale a 24:00). */
  private validateTimeSlots(rows: PriceMatrixRow[]): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!rows.length) {
      errors.push('Debe existir al menos un horario.');
      return { valid: false, errors };
    }

    const normalized = rows.map((row) => {
      const start = this.parseTimeToMinutes(row.startTime);
      const end = this.parseTimeToMinutes(row.endTime);
      return { row, start, end };
    });

    if (normalized.some(r => r.start === null || r.end === null)) {
      errors.push('Todos los horarios deben tener formato de hora válido.');
      return { valid: false, errors };
    }

    const slots = normalized
      .map(r => {
        const start = r.start as number;
        let end = r.end as number;
        if (end === 0 && start > 0) {
          end = 1440;
        }
        return {
          start,
          end,
          label: `${this.formatTime12(r.row.startTime)} - ${this.formatTime12(r.row.endTime)}`
        };
      })
      .sort((a, b) => a.start - b.start);

    slots.forEach(slot => {
      if (slot.start >= slot.end) {
        errors.push(`El horario ${slot.label} es inválido. La hora fin debe ser mayor a la de inicio.`);
      }
    });

    if (!errors.length) {
      const first = slots[0];
      if (first.start !== 0) {
        errors.push('Los horarios deben iniciar en 12:00 a.m. para cubrir las 24 horas.');
      }

      for (let i = 1; i < slots.length; i++) {
        const prev = slots[i - 1];
        const curr = slots[i];

        if (curr.start < prev.end) {
          errors.push('Los horarios no pueden sobreponerse.');
          break;
        }
        if (curr.start > prev.end) {
          errors.push('Los horarios deben cubrir las 24 horas sin espacios.');
          break;
        }
      }

      const last = slots[slots.length - 1];
      if (last.end < 1440) {
        errors.push('Los horarios deben cubrir las 24 horas del día.');
      }
    }

    return { valid: errors.length === 0, errors };
  }

  loadCatalogs(): void {
    this.loadingData.set(true);
    this.loadingService.show();

    let completed = 0;
    const total = 3;

    const checkComplete = () => {
      completed++;
      if (completed === total) {
        this.loadingData.set(false);
        this.loadingService.hide();
      }
    };

    // Cargar zonas horarias
    this.timezonesService.getAll(1, 200, {}, 'name', 1).subscribe({
      next: (response) => {
        this.timezoneOptions.set(
          response.data.map(tz => ({
            label: `${tz.name} (${(tz as any).gmt_offset ?? ''})`,
            value: tz.id
          }))
        );
        checkComplete();
      },
      error: () => checkComplete()
    });

    // Cargar zonas comerciales
    this.commercialZonesService.getAll(1, 200, {}, 'name', 1).subscribe({
      next: (response) => {
        this.commercialZoneOptions.set(
          response.data.map((cz: any) => ({
            label: cz.name,
            value: cz.id
          }))
        );
        checkComplete();
      },
      error: () => checkComplete()
    });

    // Cargar condiciones comerciales
    this.commercialConditionsService.getAll(1, 200, {}, 'name', 1).subscribe({
      next: (response) => {
        this.commercialConditionOptions.set(
          response.data.map(cc => ({
            label: cc.name,
            value: cc.id
          }))
        );
        checkComplete();
      },
      error: () => checkComplete()
    });
  }

  // ============= VALIDACIONES =============

  timeSlotValidation = computed(() => this.validateTimeSlots(this.priceMatrix()));

  isFormValid = computed(() => {
    const identifiersValid = !!(
      this.configName() &&
      this.selectedTimezone() &&
      this.selectedCommercialZone() &&
      this.selectedCommercialCondition()
    );

    const matrixValid = this.priceMatrix().length > 0 &&
      this.priceMatrix().every(row =>
        this.daysOfWeek.every(day => row.prices[day] > 0)
      );

    const validityValid = !!(
      this.validityStartDate() &&
      this.taxPercentage() >= 0 &&
      (!this.hasEndDate() || (this.validityEndDate() && this.isEndDateValid()))
    );

    return identifiersValid && matrixValid && validityValid && this.timeSlotValidation().valid;
  });

  // ============= FUNCIONES DE LA MATRIZ =============

  onTimeChange(rowId: string, field: 'startTime' | 'endTime', value: string): void {
    this.priceMatrix.update(rows => rows.map(row => {
      if (row.id === rowId) {
        return { ...row, [field]: value };
      }
      return row;
    }));
  }

  addTimeSlot(): void {
    const matrix = this.priceMatrix();
    const newId = (Math.max(...matrix.map(r => parseInt(r.id))) + 1).toString();

    const lastRow = matrix[matrix.length - 1];
    const newStartTime = lastRow.endTime;

    this.priceMatrix.update(m => [...m, {
      id: newId,
      startTime: newStartTime,
      endTime: '00:00',
      prices: { lunes: 0, martes: 0, miercoles: 0, jueves: 0, viernes: 0, sabado: 0, domingo: 0 }
    }]);
  }

  removeTimeSlot(id: string): void {
    if (this.priceMatrix().length === 1) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No se puede eliminar',
        detail: 'Debe haber al menos un horario'
      });
      return;
    }
    this.priceMatrix.update(m => m.filter(row => row.id !== id));
  }

  applyToRow(rowId: string, price: number): void {
    this.priceMatrix.update(m => m.map(row => {
      if (row.id === rowId) {
        const updatedPrices = { lunes: price, martes: price, miercoles: price, jueves: price, viernes: price, sabado: price, domingo: price };
        return { ...row, prices: updatedPrices };
      }
      return row;
    }));
  }

  applyToColumn(day: UiDayKey, price: number): void {
    this.priceMatrix.update(m => m.map(row => ({
      ...row,
      prices: { ...row.prices, [day]: price }
    })));
  }

  // Drag and Drop
  draggedPrice: number | null = null;

  onDragStart(price: number): void {
    this.draggedPrice = price;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  onDrop(rowId: string, day: UiDayKey): void {
    if (this.draggedPrice !== null) {
      this.priceMatrix.update(m => m.map(row => {
        if (row.id === rowId) {
          return { ...row, prices: { ...row.prices, [day]: this.draggedPrice! } };
        }
        return row;
      }));
      this.draggedPrice = null;
    }
  }

  // ============= GUARDAR CONFIGURACIÓN =============

  onSubmit(): void {
    if (!this.isFormValid()) {
      const slotErrors = this.timeSlotValidation().errors;
      const summary = slotErrors.length ? 'Horarios inválidos' : 'Formulario incompleto';
      const detail  = slotErrors.length ? slotErrors[0] : 'Complete todos los campos correctamente';
      this.messageService.add({ severity: 'warn', summary, detail });
      return;
    }

    this.confirmationService.confirm({
      header: 'Confirmar guardado',
      message: '¿Está seguro de guardar esta configuración de precios?',
      acceptLabel: 'Guardar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-primary',
      rejectButtonStyleClass: 'p-button-outlined p-button-secondary',
      accept: () => {
        this.saveConfiguration(false);
      }
    });
  }

  private buildPayload(confirmGaps: boolean): PriceFormData {
    const schedule: PriceScheduleFormItem[] = this.priceMatrix().map(row => {
      const prices = {} as Record<ApiDayKey, number>;
      this.daysOfWeek.forEach(uiDay => {
        const apiDay = UI_TO_API_DAY[uiDay];
        prices[apiDay] = row.prices[uiDay] ?? 0;
      });
      return {
        start_time: this.formTimeToApiString(row.startTime, false),
        end_time:   this.formTimeToApiString(row.endTime,   true),
        prices
      };
    });

    return {
      name:                     this.configName(),
      timezone_id:              this.selectedTimezone()!,
      commercial_zone_id:       this.selectedCommercialZone()!,
      commercial_condition_id:  this.selectedCommercialCondition()!,
      validity_start_date:      this.formatDateForApi(this.validityStartDate(), false),
      validity_end_date:        this.hasEndDate() && this.validityEndDate()
                                  ? this.formatDateForApi(this.validityEndDate()!, true)
                                  : null,
      tax_percentage:           this.taxPercentage(),
      is_active:                this.isActive(),
      confirm_gaps:             confirmGaps,
      schedule
    };
  }

  /** @param confirmGaps true para guardar aunque el API haya advertido huecos de vigencia. */
  private saveConfiguration(confirmGaps: boolean): void {
    const payload = this.buildPayload(confirmGaps);
    this.loadingService.show('Guardando...');

    const request$ = this.isEditMode() && this.priceId
      ? this.pricesService.update(this.priceId, payload)
      : this.pricesService.create(payload);

    request$.subscribe({
      next: () => {
        this.loadingService.hide();
        const message = this.isEditMode()
          ? 'Configuración actualizada correctamente'
          : 'Configuración guardada correctamente';
        this.router.navigate(['/price-configuration'], {
          state: { showToast: true, message, preserveListState: true }
        });
      },
      error: (err) => {
        this.loadingService.hide();

        // 409 con PRICE_CONFIG_VALIDITY_GAPS_DETECTED → mostrar modal de confirmación
        if (err?.status === 409 && err?.error?.error_code === 'PRICE_CONFIG_VALIDITY_GAPS_DETECTED') {
          this.confirmationService.confirm({
            key: 'gaps-warning',
            header: 'Advertencia: Salto en la Vigencia de Precios',
            message:
              'Se ha detectado que la configuración de precios que intentas guardar deja un hueco temporal ' +
              'con respecto a otras tarifas existentes para la misma Zona Comercial, Condición y Zona Horaria. ' +
              'Esto podría causar que haya periodos sin una tarifa definida. ' +
              '¿Deseas continuar y guardar la configuración de todos modos?',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Continuar y Guardar',
            rejectLabel: 'Cancelar',
            acceptButtonStyleClass: 'p-button-warning',
            rejectButtonStyleClass: 'p-button-outlined p-button-secondary',
            accept: () => {
              this.saveConfiguration(true);
            }
          });
          return;
        }
      }
    });
  }

  onStepBack(): void {
    this.router.navigate(['/price-configuration'], { state: { preserveListState: true } });
  }

  /** Formatea una fecha JS como ISO para el API: "YYYY-MM-DDTHH:MM:SSZ" */
  private formatDateForApi(date: Date, isEndDate: boolean): string {
    const year  = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day   = date.getDate().toString().padStart(2, '0');
    const time  = isEndDate ? 'T23:59:59Z' : 'T00:00:00Z';
    return `${year}-${month}-${day}${time}`;
  }

  public markAsTouched(fieldName: string): void {
    this.touchedFields.update(set => {
      const newSet = new Set(set);
      newSet.add(fieldName);
      return newSet;
    });
  }

  isConfigNameInvalid = computed(() => {
    return !this.configName() && this.isFieldTouched('name');
  });

  isTimezoneInvalid = computed(() => {
    return !this.selectedTimezone() && this.isFieldTouched('timezone');
  });

  isCommercialZoneInvalid = computed(() => {
    return !this.selectedCommercialZone() && this.isFieldTouched('commercialZone');
  });

  isCommercialConditionInvalid = computed(() => {
    return !this.selectedCommercialCondition() && this.isFieldTouched('commercialCondition');
  });

  isTaxPercentageInvalid = computed(() => {
    return !this.taxPercentage() && this.isFieldTouched('taxPercentage');
  });

  isStartDateInvalid = computed(() => {
    return !this.validityStartDate() && this.isFieldTouched('startDate');
  });

  isEndDateInvalid = computed(() => {
    return !this.validityEndDate() && this.isFieldTouched('endDate');
  });

  private isFieldTouched(fieldName: string): boolean {
    return this.touchedFields().has(fieldName);
  }
}
